import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'reactstrap';
import Slider from 'react-slick';
import Button from 'components/Button';
import YouTubePlayer from 'components/YoutubePlayer';
import Img from 'gatsby-image';
import LeftArrowIcon from 'static/images/icon-left-arrow.png';
import RightArrowIcon from 'static/images/icon-right-arrow.png';

const ModalCarousel = ({
  toggle,
  isOpen,
  items,
  fluidImages,
  closeBtnType = 'app-transparent-white-button',
  slideToIndex,
  video,
  showVideoFirst,
}) => {
  const [innerHtml, setInnerHtml] = useState();
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const thumbnailContainerRef = useRef(null);

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    centerMode: true,
    className: 'center',
    customPaging: function (i) {
      return fluidImages ? (
        <img
          src={`${fluidImages[i]?.fluid?.src}`}
          alt={fluidImages[i]?.alt ?? fluidImages[i]?.title ?? ''}
        />
      ) : (
        <img src={`${items[i]?.src}`} alt={items[i]?.title ?? ''} />
      );
    },
    dotsClass: 'slick-dots slick-thumb',
    useTransform: false,
  };

  const [sliderCopy, setSliderCopy] = useState(null);

  useEffect(() => {
    sliderCopy &&
      sliderCopy.slickGoTo(!showVideoFirst ? slideToIndex : slideToIndex + 1);
  }, [slideToIndex, sliderCopy]);

  const checkScrollPosition = () => {
    if (thumbnailContainerRef.current) {
      const {
        scrollLeft,
        scrollWidth,
        clientWidth,
      } = thumbnailContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    } else {
      setCanScrollLeft(false);
      setCanScrollRight(false);
    }
  };

  const scrollLeft = () => {
    if (thumbnailContainerRef.current) {
      const container = thumbnailContainerRef.current;
      const scrollAmount = Math.min(
        container.clientWidth,
        container.scrollLeft
      );
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (thumbnailContainerRef.current) {
      const container = thumbnailContainerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const scrollAmount = Math.min(
        container.clientWidth,
        maxScroll - container.scrollLeft
      );
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (isOpen) {
      // Wait for DOM to update
      setTimeout(() => {
        const thumbnailContainer = document.querySelector(
          '.app-modal-carousel .slick-thumb'
        );
        if (thumbnailContainer) {
          thumbnailContainerRef.current = thumbnailContainer;
          checkScrollPosition();
          thumbnailContainer.addEventListener('scroll', checkScrollPosition);
        }
      }, 100);
    }

    return () => {
      if (thumbnailContainerRef.current) {
        thumbnailContainerRef.current.removeEventListener(
          'scroll',
          checkScrollPosition
        );
      }
    };
  }, [isOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        setTimeout(checkScrollPosition, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  const renderVideoSlide = () => (
    <YouTubePlayer
      className={`appCarousel__video appCarousel__video--modal`}
      url={video}
      controls={true}
    />
  );

  const slides = fluidImages
    ? fluidImages.map((item, index) => (
        <div key={`carouselKeyModal${index}`} className="modalSliderContainer">
          {video && item.url ? renderVideoSlide() : <Img fluid={item.fluid} alt={item.alt ?? item.title ?? ''} />}
        </div>
      ))
    : items.map((item, index) => (
        <div key={`carouselKeyModal${index}`} className="modalSliderContainer">
          {video && item.url ? (
            renderVideoSlide()
          ) : (
            <img src={item.src} alt={item.title ?? ''} />
          )}
        </div>
      ));

  return (
    <Modal
      id="my-component"
      isOpen={isOpen}
      toggle={toggle}
      className="app-modal-carousel-container"
      backdropClassName="app-modal-carousel-backdrop"
    >
      <Button handleClick={toggle} type={closeBtnType}>
        Close
      </Button>
      <Slider
        {...settings}
        className="app-modal-carousel"
        ref={slider => setSliderCopy(slider)}
      >
        {slides}
      </Slider>

      <div
        className={`thumbnail-scroll-controls ${
          !canScrollLeft && !canScrollRight ? 'hidden' : ''
        }`}
      >
        <button
          className={`thumbnail-scroll-arrow thumbnail-scroll-left ${
            !canScrollLeft ? 'disabled' : ''
          }`}
          onClick={scrollLeft}
          disabled={!canScrollLeft}
          aria-label="Scroll thumbnails left"
        >
          <img src={LeftArrowIcon} alt="Left arrow" />
        </button>
        <button
          className={`thumbnail-scroll-arrow thumbnail-scroll-right ${
            !canScrollRight ? 'disabled' : ''
          }`}
          onClick={scrollRight}
          disabled={!canScrollRight}
          aria-label="Scroll thumbnails right"
        >
          <img src={RightArrowIcon} alt="Right arrow" />
        </button>
      </div>
    </Modal>
  );
};

ModalCarousel.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      title: PropTypes.string,
    })
  ),
  toggle: PropTypes.func,
  isOpen: PropTypes.bool,
};

export default ModalCarousel;
